/* eslint-disable no-plusplus */
/* eslint-disable camelcase */
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import 'sal.js/dist/sal.css';
import React, { useState, useEffect, useRef } from 'react';
import sal from 'sal.js';

import CtaCard from 'components/shared/cta-card';

const getUniqueValues = (arr, name) => {
  const uniqueValues = arr
    ?.flatMap((item) => item?.content?.[name])
    .filter((value, index, self) => self.indexOf(value) === index);

  return uniqueValues;
};

const filterData = (data, keywords) =>
  data.filter((item) =>
    Object.keys(keywords).every((key) => {
      if (key === 'type' || key === 'locations') {
        return item[key].some((el) => el.name === keywords[key]);
      }

      if (key === 'category') {
        return item[key].includes(keywords[key]);
      }

      return keywords[key] === item[key];
    })
  );

const variants = {
  from: {
    opacity: 0,
    height: 0,
  },
  to: {
    opacity: 1,
    height: 'auto',
  },
};

const AllProject = ({ blok }) => {
  const ITEMS_TO_SHOW = 8;
  const initProject = blok.project.flatMap((item) => [
    { ...item.content, full_slug: item.full_slug },
  ]);
  const controls = useAnimation();
  const projectContainerRef = useRef(null);
  const DURATION = 0.25;

  const [activeProjects, setActiveProjects] = useState(initProject);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({});
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [itemsToShow, setItemsToShow] = useState(ITEMS_TO_SHOW);
  const [isAnimated, setIsAnimated] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (showFilter) {
      controls.start('to');
    } else {
      controls.start('from');
    }
  }, [showFilter, controls]);

  useEffect(() => {
    if (filter) {
      const content = blok.project.flatMap((item) => [
        { ...item.content, full_slug: item.full_slug },
      ]);
      const filteredResult = filterData(content, filter);
      setActiveProjects(filteredResult);
      setItemsToShow(8);
    }
  }, [filter, blok.project]);

  useEffect(() => {
    setVisibleProjects(activeProjects.slice(0, itemsToShow));
    const timeout = setTimeout(() => {
      setIsAnimated(true);
    }, DURATION * 1000);
    return () => clearTimeout(timeout);
  }, [activeProjects, itemsToShow]);

  const handleScroll = () => {
    if (
      projectContainerRef.current &&
      projectContainerRef.current.getBoundingClientRect().bottom <= window.innerHeight + 50
    ) {
      setItemsToShow((prevItems) => prevItems + ITEMS_TO_SHOW);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (itemsToShow >= activeProjects.length) {
      setItemsToShow(activeProjects.length);
    }
  }, [itemsToShow, activeProjects]);

  useEffect(() => {
    if (visibleProjects.length > ITEMS_TO_SHOW) {
      sal({ threshold: 0.25 });
    }
  }, [visibleProjects.length]);

  const filterHandler = (name, value, target) => {
    if (target) {
      // Select all elements with the specific data-filter-name attribute
      const elements = document.querySelectorAll(`[data-filter-name="${name}"]`);

      // Loop through the elements and toggle the 'menu-animate-active' class
      elements.forEach((el) => {
        if (el.dataset.filterName === name && el !== target) {
          el.classList.remove('menu-animate-active');
        }
      });

      // Toggle the 'menu-animate-active' class on the target element
      target.classList.toggle('menu-animate-active');

      if (target.classList.contains('menu-animate-active')) {
        setIsAnimated(true);
      } else {
        setIsAnimated(false);
      }
    }

    const timeout = setTimeout(() => {
      setIsAnimated(false);
      setFilter((prevFilter) => {
        // If the category is already selected, remove it from the filter
        if (prevFilter[name] === value) {
          const { [name]: removedCategory, ...rest } = prevFilter;
          return rest;
        }

        return {
          ...prevFilter,
          [name]: value,
        };
      });
    }, DURATION * 1000);

    return () => clearTimeout(timeout);
  };

  const categoryRef = useRef(null);
  const categories = getUniqueValues(blok?.project, 'category');
  const projectTypes = getUniqueValues(blok?.project, 'type')
    .map((type) => type?.name)
    .filter((value, index, self) => self.indexOf(value) === index);
  const projectStatus = getUniqueValues(blok?.project, 'status');
  const locations = getUniqueValues(blok?.project, 'locations')
    .map((type) => type?.name)
    .filter((value, index, self) => self.indexOf(value) === index);

  const showFilternHandler = () => {
    setShowFilter(!showFilter);
  };

  const currentCategoryHandler = (target) => {
    if (target.classList.contains('menu-animate-active')) {
      setSelectedCategory(target.dataset.filter);
    } else {
      setSelectedCategory('');
    }
  };

  return (
    <div className="container py-7 lg:relative lg:grid lg:grid-cols-3 lg:py-8 xl:py-10">
      <div
        className="text-sm lg:sticky lg:bottom-0 lg:min-h-desktop-fs lg:self-end lg:text-lg xl:-bottom-[1rem] xl:text-2xl mobile:grid mobile:grid-cols-3 mobile:gap-x-5"
        {...{
          'data-sal': 'slide-up',
          'data-sal-duration': '1000',
          'data-sal-delay': '0',
          'data-sal-easing': 'ease',
        }}
      >
        <button
          className="relative mb-7 flex max-w-[4rem] items-center justify-between gap-6 text-sm leading-none lg:hidden lg:text-base xl:text-[1.375rem]"
          type="button"
          onClick={showFilternHandler}
        >
          Filter
          <span className="relative flex h-[8px] min-w-[8px]">
            <span className="absolute left-0 top-1/2 h-[1.5px] w-full -translate-y-1/2 bg-white" />
            <span
              className={`absolute left-1/2 top-0 h-full w-[1.5px] -translate-x-1/2 bg-white  transition-all duration-200 ${
                showFilter ? 'opacity-0' : 'opacity-100'
              }`}
            />
          </span>
        </button>
        <ul className="lg:mb-14 xl:mb-16 mobile:col-span-2 mobile:mb-7 mobile:flex mobile:gap-14 mobile:self-end">
          {categories?.map((category) => (
            <li key={category} className="relative flex-1">
              <button
                type="button"
                data-filter={category}
                data-filter-name="category"
                className="menu-animate capitalize"
                ref={categoryRef}
                onClick={(e) => {
                  filterHandler('category', e.target.dataset.filter, e.target);
                  currentCategoryHandler(e.target);
                }}
              >
                {category}
              </button>
            </li>
          ))}
        </ul>
        <motion.div
          initial="from"
          exit="from"
          animate={controls}
          variants={variants}
          transition={{ duration: 0.5, type: 'tween' }}
          className="lg:!block lg:!h-full lg:!opacity-100 mobile:col-span-2"
        >
          <div className="mobile:grid mobile:grid-cols-2 mobile:gap-7">
            <ul className="lg:mb-14 xl:mb-16 mobile:row-span-2 mobile:mb-14 mobile:space-y-1.5">
              {projectTypes?.map((type) => (
                <li key={type} className="relative flex-1">
                  <button
                    type="button"
                    data-filter={type}
                    data-filter-name="type"
                    className="menu-animate mobile:text-left"
                    onClick={(e) => {
                      filterHandler('type', e.target.dataset.filter, e.target);
                    }}
                  >
                    {type}
                  </button>
                </li>
              ))}
            </ul>
            <ul className="lg:mb-14 xl:mb-16 mobile:mb-14 mobile:space-y-1.5">
              {projectStatus?.map((status) => {
                const statusName = status?.split('-').join(' ');
                return (
                  <li key={status} className="relative flex-1">
                    <button
                      type="button"
                      data-filter={status}
                      data-filter-name="status"
                      className="menu-animate capitalize mobile:text-left"
                      onClick={(e) => filterHandler('status', e.target.dataset.filter, e.target)}
                    >
                      {statusName}
                    </button>
                  </li>
                );
              })}
            </ul>
            <ul className="lg:mb-14 xl:mb-16 mobile:mb-14 mobile:space-y-1.5">
              {locations?.map((location) => (
                <li key={location} className="relative flex-1">
                  <button
                    type="button"
                    data-filter={location}
                    data-filter-name="locations"
                    className="menu-animate mobile:text-left"
                    onClick={(e) => filterHandler('locations', e.target.dataset.filter, e.target)}
                  >
                    {location}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
      <div className="col-span-2" ref={projectContainerRef}>
        <div className="grid grid-cols-2 gap-5 xl:gap-8">
          <AnimatePresence>
            {visibleProjects.length > 0 &&
              isAnimated &&
              visibleProjects.map((project, index) => {
                const index1 = index + 1;
                let gridClass = 'col-span-auto';
                let aspectRatioClass = 'aspect-[520/320]';
                let isPortrait = false;
                if (index1 % 8 === 2) {
                  gridClass = 'row-span-2';
                  aspectRatioClass = 'aspect-[520/720]';
                  isPortrait = true;
                } else if (index1 % 4 === 0) {
                  gridClass = 'col-span-full';
                  aspectRatioClass = 'aspect-[530/330]';
                } else if (index1 % 8 === 5) {
                  gridClass = 'row-span-2';
                  aspectRatioClass = 'aspect-[520/720]';
                  isPortrait = true;
                }
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: DURATION, type: 'tween' }}
                    className={gridClass}
                    key={`${project._uid}-${index}`}
                    layout
                    {...(visibleProjects.length > ITEMS_TO_SHOW
                      ? {
                          'data-sal': 'slide-up',
                          'data-sal-duration': '1000',
                          'data-sal-delay': '0',
                          'data-sal-easing': 'ease',
                        }
                      : {})}
                  >
                    <CtaCard
                      data={project}
                      aspectRatioClass={aspectRatioClass}
                      slug={project.full_slug}
                      selectedCategory={selectedCategory}
                      isPortrait={isPortrait}
                      isAnimated
                    />
                  </motion.div>
                );
              })}
            {activeProjects.length === 0 && (
              <div className="col-span-full">
                <p className="text-sm font-semibold lg:text-lg xl:text-2xl">No project found</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AllProject;

AllProject.propTypes = {
  blok: PropTypes.object.isRequired,
};
